import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  RotateCcw, Package, Truck, Clock, Trash2, Mail, 
  MapPin, Calendar, CheckCircle, AlertTriangle, ExternalLink 
} from 'lucide-react';
import { RETURN_PREALERT_URL } from '../lib/links';

const ReturnProcessSection = () => {
  const { language } = useLanguage();

  const translations = {
    en: {
      label: 'Return Process',
      title: 'RETURNS',
      titleAccent: 'PROCESS',
      eligibility: {
        title: '1. Eligibility',
        description: 'Applies to packages not dispatched to destination due to:',
        reasons: [
          '[PF-5] Order canceled',
          '[PF-6] Retained at origin',
          '[PF-7] Tax payment not made',
          '[PF-9] Empty/incorrect contents—undeliverable to buyer',
          'Labels with errors or shipments exceeding service limits (oversize/overweight)'
        ]
      },
      options: {
        title: '2. Return Options',
        pickup: {
          title: '2.1 Sellers with Pick-Up Service',
          description: 'MailAmericas schedules bi-weekly return deliveries directly to the seller.'
        },
        dropoff: {
          title: '2.2 Sellers with Drop-Off',
          subtitle: 'Choose one of the following:',
          optionA: {
            title: 'A) Pick up at Warehouse',
            line1: 'You can pick up your returns at the same warehouse where the cargo was originally dropped off.',
            line2: 'Coordinate the pickup via email or form with at least 5 business days notice so your cargo is ready.',
            line2NoForm: 'Coordinate the pickup via email with at least 5 business days notice so your cargo is ready.',
            line3: 'Our Warehouse team will confirm the date and timing accordingly.'
          },
          optionB: {
            title: 'B) Ship via Preferred Courier',
            description: 'Arrange a return shipment via your preferred courier.',
            buttonText: 'Pre-Alert Your Return',
            dispatch: 'Dispatch cadence:',
            monthly: 'Monthly: First week of the month → prior month\'s packages',
            biweekly: 'Bi-weekly: First week of the month → second half of the prior month\'s packages'
          },
          sharedCta: {
            title: 'Pre-Alert your return for either option',
            subtitle: 'Coordinate pickup or shipment — same form works for both.',
            noFormTitle: 'To pre-alert your return for either option, write to our Warehouse team',
            noFormSubtitle: 'Include your packages and whether you will pick them up or ship them via courier.'
          }
        }
      },
      storage: {
        title: '3. Storage & Disposal',
        warning: 'Packages not picked up and without courier instructions will be destroyed after 15 calendar days of storage.'
      },
      contact: {
        title: '4. Contact',
        description: 'Questions about packages at origin:',
        email: 'warehouse_group@mailamericas.com'
      }
    },
    es: {
      label: 'Proceso de Devolución',
      title: 'PROCESO DE',
      titleAccent: 'DEVOLUCIONES',
      eligibility: {
        title: '1. Elegibilidad',
        description: 'Aplica a paquetes no despachados al destino debido a:',
        reasons: [
          '[PF-5] Orden cancelada',
          '[PF-6] Retenido en origen',
          '[PF-7] Pago de impuestos no realizado',
          '[PF-9] Contenido vacío/incorrecto—no entregable al comprador',
          'Etiquetas con errores o envíos que exceden límites del servicio (sobredimensionado/sobrepeso)'
        ]
      },
      options: {
        title: '2. Opciones de Devolución',
        pickup: {
          title: '2.1 Vendedores con Servicio de Recogida',
          description: 'MailAmericas programa entregas de devolución quincenales directamente al vendedor.'
        },
        dropoff: {
          title: '2.2 Vendedores con Drop-Off',
          subtitle: 'Elija una de las siguientes opciones:',
          optionA: {
            title: 'A) Recoger en Almacén',
            line1: 'Puedes retirar tus devoluciones en el mismo almacén donde entregaste la carga.',
            line2: 'Coordina el retiro vía correo o formulario con al menos 5 días hábiles de anticipación, así tu carga queda lista para ser retirada.',
            line2NoForm: 'Coordina el retiro por correo con al menos 5 días hábiles de anticipación, así tu carga queda lista para ser retirada.',
            line3: 'El equipo de Warehouse te confirmará la fecha y horario acorde.'
          },
          optionB: {
            title: 'B) Enviar vía Courier Preferido',
            description: 'Coordina un envío de devolución vía tu courier preferido.',
            buttonText: 'Pre-Alertar su Devolución',
            dispatch: 'Cadencia de despacho:',
            monthly: 'Mensual: Primera semana del mes → paquetes del mes anterior',
            biweekly: 'Quincenal: Primera semana del mes → paquetes de la segunda mitad del mes anterior'
          },
          sharedCta: {
            title: 'Pre-alerta tu devolución para cualquiera de las opciones',
            subtitle: 'Coordina el pick-up o el envío — el mismo formulario sirve para ambos.',
            noFormTitle: 'Para pre-alertar tu devolución en cualquiera de las opciones, escribe a nuestro equipo de Almacén',
            noFormSubtitle: 'Indica tus paquetes y si los retirarás o los enviarás por courier.'
          }
        }
      },
      storage: {
        title: '3. Almacenamiento y Disposición',
        warning: 'Los paquetes no recogidos y sin instrucciones de courier serán destruidos después de 15 días calendario de almacenamiento.'
      },
      contact: {
        title: '4. Contacto',
        description: 'Preguntas sobre paquetes en origen:',
        email: 'warehouse_group@mailamericas.com'
      }
    },
    pt: {
      label: 'Processo de Devolução',
      title: 'PROCESSO DE',
      titleAccent: 'DEVOLUÇÕES',
      eligibility: {
        title: '1. Elegibilidade',
        description: 'Aplica-se a pacotes não despachados ao destino devido a:',
        reasons: [
          '[PF-5] Pedido cancelado',
          '[PF-6] Retido na origem',
          '[PF-7] Pagamento de impostos não realizado',
          '[PF-9] Conteúdo vazio/incorreto—não entregável ao comprador',
          'Etiquetas com erros ou envios que excedem limites do serviço (sobredimensionado/sobrepeso)'
        ]
      },
      options: {
        title: '2. Opções de Devolução',
        pickup: {
          title: '2.1 Vendedores com Serviço de Coleta',
          description: 'MailAmericas agenda entregas de devolução quinzenais diretamente ao vendedor.'
        },
        dropoff: {
          title: '2.2 Vendedores com Drop-Off',
          subtitle: 'Escolha uma das seguintes opções:',
          optionA: {
            title: 'A) Retirar no Armazém',
            line1: 'Você pode retirar suas devoluções no mesmo armazém onde a carga foi originalmente entregue.',
            line2: 'Coordene a retirada via email ou formulário com pelo menos 5 dias úteis de antecedência para que sua carga esteja pronta.',
            line2NoForm: 'Coordene a retirada via email com pelo menos 5 dias úteis de antecedência para que sua carga esteja pronta.',
            line3: 'Nossa equipe de Armazém confirmará a data e horário conforme.'
          },
          optionB: {
            title: 'B) Enviar via Courier Preferido',
            description: 'Coordene um envio de devolução via seu courier preferido.',
            buttonText: 'Pré-Alertar sua Devolução',
            dispatch: 'Cadência de despacho:',
            monthly: 'Mensal: Primeira semana do mês → pacotes do mês anterior',
            biweekly: 'Quinzenal: Primeira semana do mês → pacotes da segunda metade do mês anterior'
          },
          sharedCta: {
            title: 'Pré-alerte sua devolução para qualquer uma das opções',
            subtitle: 'Coordene a retirada ou o envio — o mesmo formulário funciona para ambos.',
            noFormTitle: 'Para pré-alertar sua devolução em qualquer uma das opções, escreva para nossa equipe de Armazém',
            noFormSubtitle: 'Informe seus pacotes e se irá retirá-los ou enviá-los por courier.'
          }
        }
      },
      storage: {
        title: '3. Armazenamento e Descarte',
        warning: 'Pacotes não retirados e sem instruções de courier serão destruídos após 15 dias corridos de armazenamento.'
      },
      contact: {
        title: '4. Contato',
        description: 'Perguntas sobre pacotes na origem:',
        email: 'warehouse_group@mailamericas.com'
      }
    },
    zh: {
      label: '退货流程',
      title: '退货',
      titleAccent: '流程',
      eligibility: {
        title: '1. 适用范围',
        description: '适用于因以下原因未送达目的地的包裹:',
        reasons: [
          '[PF-5] 订单取消',
          '[PF-6] 在起运地被扣留',
          '[PF-7] 未缴纳税款',
          '[PF-9] 内容物为空/错误 — 无法投递给买家',
          '面单错误或超出服务限制的货物(超大/超重)'
        ]
      },
      options: {
        title: '2. 退货选项',
        pickup: {
          title: '2.1 提供取件服务的卖家',
          description: 'MailAmericas 每两周安排一次退货送货直接送达卖家。'
        },
        dropoff: {
          title: '2.2 自送货的卖家',
          subtitle: '请选择以下任一选项:',
          optionA: {
            title: 'A) 仓库自提',
            line1: '您可以在最初送货的同一仓库提取退货。',
            line2: '通过邮件或表格预约取件,提前至少 5 个工作日,以便您的货物准备就绪。',
            line2NoForm: '通过邮件预约取件,提前至少 5 个工作日,以便您的货物准备就绪。',
            line3: '我们的仓库团队将相应地确认日期和时间。'
          },
          optionB: {
            title: 'B) 通过首选快递发送',
            description: '通过您首选的快递商安排退货发送。',
            buttonText: '预报您的退货',
            dispatch: '发货节奏:',
            monthly: '每月:月初第一周 → 上月的包裹',
            biweekly: '双周:月初第一周 → 上月下半月的包裹'
          },
          sharedCta: {
            title: '为任一选项预报您的退货',
            subtitle: '协调取件或发货 — 同一个表格适用于两种方式。',
            noFormTitle: '如需为任一选项预报退货,请发送邮件至我们的仓库团队',
            noFormSubtitle: '请说明您的包裹,以及您将自提还是通过快递发送。'
          }
        }
      },
      storage: {
        title: '3. 存储与处置',
        warning: '未提取且没有快递指示的包裹将在存储 15 个日历日后被销毁。'
      },
      contact: {
        title: '4. 联系',
        description: '关于起运地包裹的问题:',
        email: 'warehouse_group@mailamericas.com'
      }
    }
  };

  const content = translations[language] || translations.en;

  return (
    <section
      id="returns"
      data-testid="returns-section"
      className="py-24 md:py-32 bg-gray-50 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="eyebrow mb-4">
            <RotateCcw className="w-4 h-4" />
            {content.label}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
            {content.title}{' '}
            <span className="text-[#7AC142]">{content.titleAccent}</span>
          </h2>
        </motion.div>

        <div className="space-y-12">
          {/* 1. Eligibility */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white border-2 border-gray-200 p-8"
          >
            <h3 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
              <Package className="w-6 h-6 text-[#7AC142]" />
              {content.eligibility.title}
            </h3>
            <p className="text-gray-600 mb-4">{content.eligibility.description}</p>
            <ul className="space-y-2">
              {content.eligibility.reasons.map((reason, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-[#7AC142] mt-0.5 flex-shrink-0" />
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* 2. Return Options */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white border-2 border-gray-200 p-8"
          >
            <h3 className="text-2xl font-bold text-black mb-6 flex items-center gap-3">
              <Truck className="w-6 h-6 text-[#7AC142]" />
              {content.options.title}
            </h3>

            {/* 2.1 Pick-Up Service */}
            <div className="mb-8 pl-4 border-l-4 border-[#7AC142]">
              <h4 className="text-lg font-bold text-black mb-2">{content.options.pickup.title}</h4>
              <p className="text-gray-600">{content.options.pickup.description}</p>
            </div>

            {/* 2.2 Drop-Off */}
            <div className="pl-4 border-l-4 border-gray-300">
              <h4 className="text-lg font-bold text-black mb-2">{content.options.dropoff.title}</h4>
              <p className="text-gray-600 mb-6">{content.options.dropoff.subtitle}</p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Option A */}
                <div className="bg-gray-50 p-6 border border-gray-200">
                  <h5 className="font-bold text-black mb-4">{content.options.dropoff.optionA.title}</h5>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-[#7AC142] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{content.options.dropoff.optionA.line1}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Calendar className="w-4 h-4 text-[#7AC142] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {RETURN_PREALERT_URL ? content.options.dropoff.optionA.line2 : content.options.dropoff.optionA.line2NoForm}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#7AC142] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{content.options.dropoff.optionA.line3}</span>
                    </div>
                  </div>
                </div>

                {/* Option B */}
                <div className="bg-gray-50 p-6 border border-gray-200">
                  <h5 className="font-bold text-black mb-4">{content.options.dropoff.optionB.title}</h5>
                  <p className="text-gray-700 text-sm mb-4">{content.options.dropoff.optionB.description}</p>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    {content.options.dropoff.optionB.dispatch}
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• {content.options.dropoff.optionB.monthly}</li>
                    <li>• {content.options.dropoff.optionB.biweekly}</li>
                  </ul>
                </div>
              </div>

              {/* Shared CTA — applies to BOTH Option A and Option B */}
              <div className="mt-6 bg-gradient-to-r from-[#7AC142]/10 via-[#7AC142]/5 to-transparent border-l-4 border-[#7AC142] p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#7AC142] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="font-bold text-black">
                      {RETURN_PREALERT_URL ? content.options.dropoff.sharedCta.title : content.options.dropoff.sharedCta.noFormTitle}
                    </p>
                    <p className="text-sm text-gray-600">
                      {RETURN_PREALERT_URL ? content.options.dropoff.sharedCta.subtitle : content.options.dropoff.sharedCta.noFormSubtitle}
                    </p>
                  </div>
                </div>
                {RETURN_PREALERT_URL ? (
                  <a
                    href={RETURN_PREALERT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#7AC142] text-black px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-[#69A835] transition-colors whitespace-nowrap"
                    data-testid="return-form-btn"
                  >
                    {content.options.dropoff.optionB.buttonText}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                ) : (
                  <a
                    href={`mailto:${content.contact.email}`}
                    className="font-mono font-bold text-black border-b-2 border-[#7AC142] break-all lg:break-normal lg:whitespace-nowrap lg:shrink-0 hover:text-[#4d7f24] transition-colors"
                    data-testid="return-contact-link"
                  >
                    {content.contact.email}
                  </a>
                )}
              </div>
            </div>
          </motion.div>

          {/* 3. Storage & Disposal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-yellow-50 border-2 border-yellow-200 p-8"
          >
            <h3 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
              <Trash2 className="w-6 h-6 text-yellow-600" />
              {content.storage.title}
            </h3>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
              <p className="text-yellow-800 font-medium">{content.storage.warning}</p>
            </div>
          </motion.div>

          {/* 4. Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-black text-white p-8"
          >
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <Mail className="w-6 h-6 text-[#7AC142]" />
              {content.contact.title}
            </h3>
            <p className="text-gray-400 mb-2">{content.contact.description}</p>
            <a 
              href={`mailto:${content.contact.email}`}
              className="text-[#7AC142] hover:underline font-mono text-lg break-all"
            >
              {content.contact.email}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ReturnProcessSection;
